const CONFIG = require('./config.json');
const BoardStateDto = require('./boardStateDto');
const CellDomain = require('./cellDomain');
const komaResource = require('./resource');

/**
 * @typedef {import('./cellDomain').CellDomain} CellDomain
 * @typedef {import('./boardStateDto').BoardStateDto} BoardStateDto
 */

module.exports={
    /**
     * 自分か相手の駒を判断
     * 
     * @param {String} koma 駒を表現するString('2s')
     * @param {Number} playerSide 先手１後手２
     * @returns 相手の駒はTrue, 自分の駒はFalse
     */
    isEnemy: function(koma,playerSide){
        return koma.slice(0,1)!=playerSide;
    },
    /**
     * 敵のplayerSideを算出
     * 
     * @param {Number} playerSide 自分のplayerSide、先手１後手２
     * @returns 相手のplayerSide
     */
    resolveEnemySide: function(playerSide){
        return playerSide==='1'? '2':'1';
    },
    /**
     * 駒のフールネームを返す
     * 
     * @param {String} koma 駒を表現するString('2s')
     * @returns 駒のフルネーム
     */
    resolveKomaName: function(koma){
        if (!koma && koma==="") return "empty";
        // 最初の数字（先手後手）を除く
        const komaAbbre=koma.slice(1);
        return komaResource[komaAbbre].name;
    },
    /**
     * 駒の番号から、盤面上のRow数を算出
     * 
     * @param {Number} index 盤面上の駒の番号（0-80）
     * @returns 駒が何行目にいるのか
     */
    convertindexToRow: function(index){
        return Math.floor(index/9);
    },
    /**
     * 駒の番号から、盤面上のColumn数を算出
     * 
     * @param {Number} index 盤面上の駒の番号（0-80）
     * @returns 駒が何列目にいるのか
     */
    convertindexToColumn: function (index){
        const row=Math.floor(index/9);
        return index-row*9;
    }, 
    /**
     * 駒の盤面上のRow,column数から駒の番号を算出
     * 
     * @param {Number} row 行数
     * @param {Number} column 列数
     * @returns 盤面上の駒の番号（0-80）
     */
    convertRowColumnToindex: function (row,column){
        return 9*row+column;
    },
    /**
     * 選択された駒の移動範囲を算出
     * 
     * @param {CellDomain} clickedCell 現在選択されたセル
     * @param {BoardStateDto} state 現在の盤面
     * @param {Number} playerSide 自分のplayerSide、先手１後手２
     * @returns 移動できる範囲（Index表示）
     */
    calLegalMove: function (clickedCell,state,playerSide){
        if(!clickedCell){
            return;
        }
        const legalMoveCellindex=[];
        // 移動できる範囲を追加
        // komaBoardの駒は自由に動ける
        if(clickedCell.boardName==='komaBoard'){
            for(const [i,v] of this.flattenLayout(state.layout).entries()){
                if (v==='') legalMoveCellindex.push(i);
            }
            return legalMoveCellindex;
        }
        for(let move of CONFIG.movement[clickedCell.komaName]){
            if(typeof move ==='number'){
            let row=this.convertindexToRow(clickedCell.index+move);
            let column=this.convertindexToColumn(clickedCell.index+move);
            this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide);
            } else if(move === 'up'){
            for(let i=1;;i++){
                let row=clickedCell.row-i;
                let column=clickedCell.column;
                if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                    break;
                }
            }
            } else if(move === 'down'){
            for(let i=1;;i++){
                let row=clickedCell.row+i;
                let column=clickedCell.column;
                if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                    break;
                }
            }
            } else if(move === 'left'){
            for(let i=1;;i++){
                let row=clickedCell.row;
                let column=clickedCell.column-i;
                if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                    break;
                }
            }
            } else if(move === 'right'){
            for(let i=1;;i++){
                let row=clickedCell.row;
                let column=clickedCell.column+i;
                if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                    break;
                }
            }
            } else if(move === 'upleft'){
            for(let i=1;;i++){
                let row=clickedCell.row-i;
                let column=clickedCell.column-i;
                if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                    break;
                }
            }
            } else if(move === 'upright'){
            for(let i=1;;i++){
                let row=clickedCell.row-i;
                let column=clickedCell.column+i;
                if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                    break;
                }
            }
            } else if(move === 'downleft'){
                for(let i=1;;i++){
                    let row=clickedCell.row+i;
                    let column=clickedCell.column-i;
                    if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                        break;
                    }
                }
            } else if(move === 'downright'){
                for(let i=1;;i++){
                    let row=clickedCell.row+i;
                    let column=clickedCell.column+i;
                    if(!this.$verifyAndAddindex(row,column,legalMoveCellindex,state,playerSide)){
                    break;
                    }
                }
            }
        }
        return legalMoveCellindex;
    },
    /**
     * cellObjectからlayout上のString('2s'など)を算出
     * 
     * @param {CellDomain} cellObject セル
     * @param {BoardStateDto} state 盤面
     * @returns {String}セル中の駒を表現するString
     */
    resolveStateString: function(cellObject,state){
        if(cellObject.boardName==='komaBoard'){
            if(state.playerSide==='1') return state.capturedPlayer2KomaList[cellObject.index];
            else return state.capturedPlayer1KomaList[cellObject.index];
        }
        return state.layout[cellObject.row][cellObject.column];
    },
    // 
    /**
     * 選択された駒の移動先は盤面外や、自分の駒があるなどの制限をチェックし、問題なければそれを移動先のリストに追加。
     * 
     * @param {Number} row 行
     * @param {Number} column 列 
     * @param {Number} legalMoveCellindex 
     * @param {BoardStateDto} state 盤面
     * @param {Number} playerSide 自分のplayerSide、先手１後手２
     * @returns 飛車など範囲的な移動ができる駒で、次のセルも検証する時のみTrue
     */
    $verifyAndAddindex:function(row,column,legalMoveCellindex,state,playerSide){
        try{
          const komaInCell=state.layout[row][column];
          // 相手の駒
          if(komaInCell.includes(this.resolveEnemySide(playerSide))){
            legalMoveCellindex.push(this.convertRowColumnToindex(row,column));
            return false;
          // 自分の駒
          } else if (komaInCell.includes(playerSide)){
            return false;
          // 空
          } else {
            legalMoveCellindex.push(this.convertRowColumnToindex(row,column));
            return true;
          }
        } catch(err){
          if(err.name==='RangeError'){
            return false;
          }
        }
    },
    /**
     * 任意のアイテムを返す
     * 
     * @param {Array|Map} array 
     * @returns Array|Mapの任意のアイテム
     */
    $pickRandomItem(arrayOrMap){
        if(Array.isArray(arrayOrMap)){
            return arrayOrMap[Math.floor(Math.random()*arrayOrMap.length)];
        }
        // Mapの場合、Key,Valueを返す
        if(arrayOrMap instanceof Map){
            const keysArray=[...Array.from(arrayOrMap.keys())];
            const randomKeyOfMap=keysArray[Math.floor(Math.random()*arrayOrMap.size)];
            return [arrayOrMap.get(randomKeyOfMap),randomKeyOfMap];
        }
    },
    /**
     * 相手から見る盤面を自分から見る盤面に変換
     * 
     * @param {BoardStateDto} boardState 
     * @returns {BoardStateDto} 自分から見る盤面
     */
    revertBoardViewSide:function(boardState){
        let reversedFlatLayout=this.flattenLayout(boardState.layout).reverse();
        return new BoardStateDto (
            this.dimensionalizeFlatLayout(reversedFlatLayout),
            boardState.capturedPlayer2KomaList,
            boardState.capturedPlayer1KomaList,
            // playerSideは「誰による盤面」を表現するため、ここでは変えない
            boardState.playerSide
        );
    },
    /**
     * 2D盤面をフラットにする
     * 
     * @param {Array} multiRowLayout 
     * @returns {Array} 一次元の盤面bannmenn
     */
    flattenLayout:function(multiRowLayout){
        let flatLayout=[];
          for(const row of multiRowLayout){
              for(const koma of row){
                  flatLayout.push(koma);
              }
          }
        return flatLayout;
    },
    /**
     * フラットな盤面を2Dにする
     * 
     * @param {Array} flatLayout 
     * @returns {Array} 2D盤面
     */
    dimensionalizeFlatLayout:function(flatLayout){
        let multiRowLayout=[];
        for(let index=0;index<flatLayout.length;index=index+9){
            multiRowLayout.push(flatLayout.slice(index,index+9));
        }
        return multiRowLayout;
    },
    /**
     * Judge if a koma inside cell is promotable
     * View if always fixed to: bottom=>Self side
     * 
     * @param {String} komaName
     * @param {Number} index 
     * @returns {Boolean} true if the koma in cell is promotable
     */
    isPromoable: function(komaName,index){
        const ENEMY_ZONE=27;
        // Koma doesn't possess promoted state
        if(['osho','kinsho'].includes(komaName)||this.isPromoted({komaName})){
            return false;
        }
        if(index<ENEMY_ZONE){
            return true;
        }
    },
    /**
     * promote the selected cell
     * 
     * @param {CellDomain} cellDomain Promoted cellDomain(afterMove)
     * @param {BoardStateDto} boardStateDto 
     * 
     * @return {BoardStateDto} newState state after promotion
     */
    promote: function(cellDomain,boardStateDto){
        const promotedStateString=this.resolveStateString(cellDomain,boardStateDto).slice().concat('p');
        const newBoardState=this.deepCopyObject(boardStateDto);
        newBoardState.layout[cellDomain.row][cellDomain.column]=promotedStateString;
        return newBoardState;
    },
    /**
     * return true if a cell is already promoted
     * 
     * @param {CellDomain} cellDomain 
     */
    isPromoted: function({komaName}){
        for(const koma in komaResource){
            if(komaResource[koma].name===komaName){
                return komaResource[koma].promoted;
            }
        }
    },
    /**
     * deep copy of array
     * 
     * @param {Array} arr 
     * @returns {Array} copied array
     */
    deepCopy:function(arr) {
        let copy = [];
        arr.forEach(elem => {
          if(Array.isArray(elem)){
            copy.push(this.deepCopy(elem))
          }else{
            if (typeof elem === 'object') {
              copy.push(this.deepCopyObject(elem))
          } else {
              copy.push(elem)
            }
          }
        })
        return copy;
    },
    /**
     * deep copy of obj
     * 
     * @param {any} obj 
     * @returns {any} oby
     */
    deepCopyObject:function(obj){
        let tempObj = {};
        for (let [key, value] of Object.entries(obj)) {
        if (Array.isArray(value)) {
            tempObj[key] = this.deepCopy(value);
        } else {
            if (typeof value === 'object') {
            tempObj[key] = this.deepCopyObject(value);
            } else {
            tempObj[key] = value
            }
        }
        }
        return tempObj;
    }
}
